import React from 'react';
import type { TFunction } from 'i18next';

import PopoverText from '../../../app/components/PopoverText';
import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';
import { GetEmailDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import {
  EmailStatus,
  EmailStatusObj,
  SentEmailTypeObj,
} from '../../../common/pokCore/validation/enums';
import { TKeys, useTranslation } from '../../../translations';

import { EmailPreviewLink } from './EmailPreviewLink';
import { EmailResendLink } from './EmailResendLink';
import { EmailResendLinkWithOverride } from './EmailResendLinkWithOverride';
import { EmailStatusIcon } from './EmailStatusIcon';
import { EmailHistoryLink } from './EmailHistoryLink';

interface EmailsProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  refresh?: boolean;
}

export const emailForGrid = (
  email: GetEmailDto,
  t: TFunction<'applicationLng', 'applicationLng'>,
  tk: TKeys<'applicationLng'>,
  setRefresh?: () => void | undefined,
  setLoading?: (loading: boolean) => void,
) => ({
  key: email.id,
  values: {
    'email.sendDate': momencik(email.sendDate, 'YYYY-MM-DD HH:mm:ss'),
    'email.sentFrom': email.sentFrom || '',
    'email.sentTo': [
      email.sentTo || '',
      email.sentCc ? ` ${t(tk.email.cc)}: ${email.sentCc}` : '',
      email.sentBcc ? ` ${t(tk.email.bcc)}: ${email.sentBcc}` : '',
    ]
      .filter(Boolean)
      .join(' '),
    'email.subject': email.subject || '',
    type_text: email.emailType ? t(SentEmailTypeObj[email.emailType]) : '',
    company_shortname: email.company?.shortname || '',
    employee_name: email.owner?.name || '',
    status: (
      <div className="d-flex align-items-center justify-content-center gap-2">
        <EmailStatusIcon
          status={email.status as unknown as EmailStatus}
          t={t}
        />
        {email.status !== EmailStatus.Sent && email.sentMessageInfo && (
          <PopoverText
            bodyText={email.sentMessageInfo}
            delay={true}
            className="icon-red mt-2"
          />
        )}
      </div>
    ),
    actions: (
      <div className="email-row-actions">
        <EmailPreviewLink emailId={email.id} />
        <EmailResendLink
          emailId={email.id}
          sendDate={email.sendDate}
          setRefresh={setRefresh}
          setLoading={setLoading}
        />
        <EmailResendLinkWithOverride
          emailId={email.id}
          email={email}
          setRefresh={setRefresh}
          setLoading={setLoading}
        />
        {(email.parentEmailId || email.hasResends) && (
          <EmailHistoryLink emailId={email.id} />
        )}
      </div>
    ),
  },
  exportValues: {
    status: email.status ? t(EmailStatusObj[email.status]) : '',
    actions:
      email.status !== EmailStatus.Sent && email.sentMessageInfo
        ? email.sentMessageInfo
        : '',
  },
});

export const Emails: React.FC<EmailsProps> = props => {
  const { t, tk } = useTranslation('applicationLng');
  const columns = [
    { header: t(tk.email.sendDate), property: 'email.sendDate' },
    { header: t(tk.email.sentFrom), property: 'email.sentFrom' },
    { header: t(tk.email.sentTo), property: 'email.sentTo' },
    { header: t(tk.email.subject), property: 'email.subject' },
    { header: t(tk.email.type), property: 'type_text' },
    { header: t(tk.common.company), property: 'company_shortname' },
    { header: t(tk.common.employee), property: 'employee_name' },
    {
      header: t(tk.common.status),
      property: 'status',
      noSorting: true,
      noFilter: true,
    },
    { header: '', property: 'actions', noSorting: true, noFilter: true },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderDirection="DESC"
      initialOrderColumn="email.sendDate"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.email.noData)}
      refresh={props.refresh}
      withFilterInColumns={true}
      showExport={true}
    />
  );
};
